import React, { Fragment, useEffect, useState } from "react";
import { toastr } from "react-redux-toastr";
import {
  City,
  CityZone,
  CityLocation,
  RequirementResponseData,
} from "../../../core";
import "react-widgets/dist/css/react-widgets.css";
import api from "../../../api";
import EditRequirementForm from "./EditRequirementForm";
import {
  LeadRequirement,
  RequirementStatus,
} from "../../../core/api/models/requirement";

interface ObjectType {
  value: string;
  name: string;
}

interface FormData {
  city: ObjectType;
  microMarkets: string[];
  preferredLocations: string[];
  propertyType: ObjectType;
  projectType: ObjectType;
  minPrice: ObjectType;
  maxPrice: ObjectType;
  minArea: string;
  maxArea: string;
  earliestMoveInDate: string;
  lastMoveInDate: string;
  floorPreferences: string[];
  preferedProjects: string[];
  preferredDevelopers: string[];
  constructionStatus: string[];
  carParking: string;
  powerBackup: string;
  grade: string;
  // certification: string[];
  amenities: string[];
  // leaseExpiry: string;
}

interface LoadingStates {
  cityLoading: boolean;
  cityZoneLoading: boolean;
  prefLocLoading: boolean;
  propertyTypeLoading: boolean;
  projectTypeLoading: boolean;
  floorPrefLoading: boolean;
  prefProjLoading: boolean;
  prefDevLoading: boolean;
  amenitiesLoading: boolean;
  facilityTypeLoading: boolean;
}

const initialData = {
  city: "",
  microMarkets: [],
  preferredLocations: [],
  propertyType: "",
  projectType: "",
  minPrice: "",
  maxPrice: "",
  minArea: "",
  maxArea: "",
  earliestMoveInDate: "",
  lastMoveInDate: "",
  floorPreferences: [],
  preferedProjects: [],
  preferredDevelopers: [],
  constructionStatus: [],
  carParking: "",
  powerBackup: "",
  grade: "",
  certification: [],
  amenities: [],
  weightage: "",
  // leaseExpiry: '',
};

const EditRequirement: React.FC<{
  requirement?: LeadRequirement;
  leadId?: string;
  customerId?: string;
  onClose?: () => void;
  className?: string;
  onReload?: (id: string) => void;
}> = (props) => {
  const [cities, setCities] = useState<any>([]);
  const [microMarketsData, setMicroMarketsData] = useState<any>([]);
  const [preferredLocationsData, setPreferredLocationsData] = useState<any>([]);
  const [projectType, setProjectType] = useState<any>([]);
  const [propertyType, setPropertyType] = useState<any>([]);
  // const [preferredProjectType, setPreferredProjectType] = useState<any>([]);
  const [preferredDevelopers, setPreferredDevelopers] = useState<any>([]);
  const [floorPreferences, setFloorPreferences] = useState<any>([]);
  const [preferredProject, setPeferredProject] = useState<any>([]);
  const [amenities, setAmenities] = useState<any>([]);
  const [facilities, setFacilities] = useState<any>([]);
  const [isRequirementEdit, setIsRequirementEdit] = useState<boolean>(false);
  const [
    confirmResData,
    setConfirmResData,
  ] = useState<RequirementResponseData>();
  const [approvalButtonDiabled, setApprovalButtonDiabled] = useState<boolean>(
    false
  );
  const [enableActionButton, setEnableActionButton] = useState<boolean>(false);
  const [parentId, setParentId] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [isFormChanged, setIsFormChanged] = useState<boolean>(true);
  const [loadingStates, setLoadingStates] = useState<LoadingStates>({
    cityLoading: true,
    cityZoneLoading: true,
    prefLocLoading: true,
    propertyTypeLoading: true,
    projectTypeLoading: true,
    floorPrefLoading: true,
    prefProjLoading: true,
    prefDevLoading: true,
    amenitiesLoading: true,
    facilityTypeLoading: true,
  });

  const [saving, setSaving] = useState<boolean>(false);

  const decodeHtmlEntities = (html: string) => {
    const textarea = document.createElement("textarea");
    textarea.innerHTML = html;
    return textarea.value;
  };

  useEffect(() => {
    const fetchCityData = async () => {
      try {
        const cityData = await api.AddNewRequirement.loadAllCities();
        const cityOptions = cityData?.map((item: City) => ({
          value: item._id,
          name: decodeHtmlEntities(item.cityName),
        }));

        cityOptions.sort((a, b) => a.name.localeCompare(b.name));
        setCities(cityOptions);
      } catch (error) {
        // console.log("Failed to fetch city data:", error);
      } finally {
        setLoadingStates((prevState) => ({ ...prevState, cityLoading: false }));
      }

      try {
        const floorData = await api.AddNewRequirement.getPreferredFloor();
        const floorDataOptions = (floorData || [])
          .map((item: any) => ({
            value: item,
            name: item,
          }))
          .sort((a: { name: number }, b: { name: number }) => a.name - b.name);

        setFloorPreferences(floorDataOptions || []);
      } catch (error) {
        // console.log("Failed to fetch preferred floor:", error);
      } finally {
        setLoadingStates((prevState) => ({
          ...prevState,
          floorPrefLoading: false,
        }));
      }

      try {
        const developerData = await api.AddNewRequirement.getPreferredDevelopers(
          { search: "" }
        );
        const developerDataOptions = developerData?.map((item: any) => ({
          value: item._id,
          name: decodeHtmlEntities(item.developer),
        }));
        setPreferredDevelopers(developerDataOptions);
      } catch (error) {
        // console.log("Failed to fetch preferred developers:", error);
      } finally {
        setLoadingStates((prevState) => ({
          ...prevState,
          prefDevLoading: false,
        }));
      }

      try {
        const amenityData = await api.AddNewRequirement.getAmenity({
          search: "",
        });
        const amenityDataOptions = amenityData?.map((item: any) => ({
          value: item._id,
          name: decodeHtmlEntities(item.amenity),
        }));
        setAmenities(amenityDataOptions);
      } catch (error) {
        // console.log("Failed to fetch amenities data:", error);
      } finally {
        setLoadingStates((prevState) => ({
          ...prevState,
          amenitiesLoading: false,
        }));
      }

      try {
        const facilityData = await api.AddNewRequirement.getFacilityType();
        const facilityOptions = facilityData?.map((item: any) => ({
          value: item._id,
          name: decodeHtmlEntities(item?.facilityType),
        }));
        setFacilities(facilityOptions);
      } catch (error) {
        // console.log("Failed to fetch facilityType data:", error);
      } finally {
        setLoadingStates((prevState) => ({ ...prevState, facilityTypeLoading: false }));
      }

    };
    handlePropertyType();
    handleProjectType();

    fetchCityData();
  }, []);

  React.useEffect(() => {
    if (props.requirement?._id) {
      handleCityZones(props.requirement.city);
      handlePreferredLocationsChange(props.requirement.city);
      handlePreferredProjectChange(props.requirement.city);
    }
  }, [props.requirement?._id]);

  const handlePreferredProjectChange = async (selectedCityId: string) => {
    try {
      const projectData = await api.AddNewRequirement.getPreferredProject({
        cityId: selectedCityId,
      });
      const projectDataOptions = projectData?.map((item: any) => ({
        value: item._id,
        name: decodeHtmlEntities(item?.projectName),
      }));
      setPeferredProject(projectDataOptions);
    } catch (error) {
      // console.log("Failed to fetch preferred project:", error);
    } finally {
      setLoadingStates((prevState) => ({
        ...prevState,
        prefProjLoading: false,
      }));
    }
  };

  const handleCityZones = async (selectedCityId: string) => {
    handlePreferredLocationsChange(selectedCityId);
    // handlePropertyType(selectedCityId);
    // handleProjectType();
    try {
      const microMarketsListData = await api.AddNewRequirement.getCityZones(
        selectedCityId
      );
      const cityZoneOptions = microMarketsListData?.map((item: CityZone) => ({
        value: item._id,
        name: decodeHtmlEntities(item.cityZoneName),
        cityLocations: item.cityLocations || [],
      }));
      setMicroMarketsData(cityZoneOptions);
    } catch (error) {
      // console.log("Failed to fetch micro markets data:", error);
    } finally {
      setLoadingStates((prevState) => ({
        ...prevState,
        cityZoneLoading: false,
      }));
    }
  };

  // Load preferred locations data from API based on selected city
  const handlePreferredLocationsChange = async (selectedCityId: string) => {
    try {
      const preferredLocationsListData = await api.AddNewRequirement.getPreferedLocations(
        selectedCityId
      );
      const cityPreferedLocOptions = preferredLocationsListData?.map(
        (item: CityLocation) => ({
          value: item._id,
          name: decodeHtmlEntities(item.cityLocName),
        })
      );
      setPreferredLocationsData(cityPreferedLocOptions);
    } catch (error) {
      // console.log("Failed to fetch preferred locations data:", error);
    } finally {
      setLoadingStates((prevState) => ({
        ...prevState,
        prefLocLoading: false,
      }));
    }
  };

  // Load property type data from API based on selected city
  const handlePropertyType = async () => {
    try {
      const propertyTypeData = await api.AddNewRequirement.getPropertyType();
      // let propertyTypeOptions = propertyTypeData?.map((item: CityLocation) => ({
      //     value: item.cityDetailsID,
      //     name: item.cityLocName,
      // }));

      const subTypes = propertyTypeData.reduce((acc: any, item: any) => {
        acc.push(
          ...item.subTypes.map(
            (subType: { _id: any; propertyTypeName: any }) => ({
              value: subType._id,
              name: decodeHtmlEntities(subType.propertyTypeName),
            })
          )
        );
        return acc;
      }, []);
      setPropertyType(subTypes);
      // setValue('preferredLocations', []);
    } catch (error) {
      // console.log("Failed to fetch property type data:", error);
    } finally {
      setLoadingStates((prevState) => ({
        ...prevState,
        propertyTypeLoading: false,
      }));
    }
  };

  const handleProjectType = async () => {
    try {
      const projectTypeData = await api.AddNewRequirement.getProjectType();
      const subTypes = projectTypeData.reduce((acc: any, item: any) => {
        if (item?.subTypes) {
          acc.push(
            ...item.subTypes.map(
              (subType: { _id: any; propertyTypeName: any }) => ({
                value: subType._id,
                name: decodeHtmlEntities(subType.propertyTypeName),
              })
            )
          );
        }
        return acc;
      }, []);
      setProjectType(subTypes);
    } catch (error) {
      // console.log("Failed to fetch project type data:", error);
    } finally {
      setLoadingStates((prevState) => ({
        ...prevState,
        projectTypeLoading: false,
      }));
    }
  };

  const getFormatedFormData = (fData: any) => {
    return {
      city: fData.city,
      microMarkets: fData.microMarkets,
      preferredLocations: fData.preferredLocations,
      propertyType: fData.propertyType || undefined,
      projectType: fData.projectType || undefined,
      // minPrice:
      //   (typeof fData.minPrice === "object"
      //     ? Number(fData?.minPrice?.value)
      //     : Number(fData?.minPrice)) || undefined,
      // maxPrice:
      //   (typeof fData.maxPrice === "object"
      //     ? Number(fData?.maxPrice?.value)
      //     : Number(fData?.maxPrice)) || undefined,
      minPrice: fData.minPrice || undefined,
      maxPrice: fData.maxPrice || undefined,
      minArea: fData.minArea || undefined,
      maxArea: fData.maxArea || undefined,
      // earliestMoveInDate: fData.earliestMoveInDate
      //   ? new Date(Date.parse(fData.earliestMoveInDate))
      //   : undefined,
      // lastMoveInDate: fData.lastMoveInDate ? new Date(Date.parse(fData.lastMoveInDate)) : undefined,
      earliestMoveInDate: fData?.earliestMoveInDate || undefined,
      lastMoveInDate: fData?.lastMoveInDate || undefined,
      floorPreferences: fData.floorPreferences || undefined,
      preferedProjects: fData.preferedProjects || undefined,
      preferredDevelopers: fData.preferredDevelopers || undefined,
      constructionStatus: fData.constructionStatus || undefined,
      proposalFormatType: fData.proposalFormatType || undefined,
      proposalContentType: fData.proposalContentType || undefined,
      constructionCompletionDate: fData.constructionCompletionDate || undefined,
      includeGradeC: fData.includeGradeC || false,
      includeLeaseExpiry: fData.includeLeaseExpiry || false,
      // carParking:
      //   fData.carParking !== undefined
      //     ? fData.carParking === "yes"
      //     : fData.carParking === "no"
      //       ? false
      //       : undefined,
      // powerBackup:
      //   fData.powerBackup !== undefined
      //     ? fData.powerBackup === "yes"
      //     : fData.powerBackup === "no"
      //       ? false
      //       : undefined,
      // grade: fData.grade || undefined,
      amenities: fData.amenities || undefined,
      certainity: fData.certainity || undefined,
      forecast: fData.forecast || undefined,
      weightage: fData.weightage || undefined,
      interiorType: fData?.interiorType?.map((item: any) => item?.value || item) || undefined,
      facilityType: fData?.facilityType || undefined,
      proposalDueBy: fData.proposalDueBy
        ? new Date(Date.parse(fData.proposalDueBy))
        : undefined,
      minSeats: fData.minSeats || undefined,
      maxSeats: fData.maxSeats || undefined,
      specialRequirements: fData?.specialRequirements || undefined,
      coreRequirements: fData?.coreRequirements || undefined,
      coreRequirementParameters: fData?.coreRequirementParameters || undefined,
      leadId: props.requirement?.leadId,
      // customerId: props.requirement?.customerId,
      parentId,
      ...(props?.requirement?.companyId
        ? { companyId: props?.requirement?.companyId }
        : { customerId: props.requirement?.customerId }),
    };
  };

  // Function to check if two values are equal
  const areValuesEqual = (value1: any, value2: any) => {
    // Handle special cases (e.g., true/false for carParking and powerBackup)
    if (typeof value1 === "boolean" && typeof value2 === "string") {
      return value1 === (value2 === "yes");
    } else if (typeof value1 === "string" && typeof value2 === "boolean") {
      return value2 === (value1 === "yes");
    } else if (Array.isArray(value1) && Array.isArray(value2)) {
      // Compare arrays by checking if their stringified representations are equal
      return JSON.stringify(value1) === JSON.stringify(value2);
    }

    // Compare other values
    return value1 === value2;
  };

  // Function to compare two objects and find the changed keys
  const findChangedKeys = (obj1: any, obj2: any) => {
    const allKeys = Array.from(
      new Set([...Object.keys(obj1), ...Object.keys(obj2)])
    );
    const changedKeys = [];

    for (const key of allKeys) {
      if (key === "parentId") {
        continue; // Skip 'parentId' key
      }

      const value1 = obj1[key];
      const value2 = obj2[key];

      // Check if the key exists in formData but not in requirementData
      if (!(key in obj1) && key in obj2 && value2 !== undefined) {
        changedKeys.push(key);
      }
      // Check if the values of the common keys are different
      else if (key in obj1 && key in obj2 && !areValuesEqual(value1, value2)) {
        changedKeys.push(key);
      }
    }

    return changedKeys;
  };

  const onSubmit = async (data: FormData) => {
    setSaving(true);
    // Call your API to save the form data
    const formatedFormData = getFormatedFormData(data);
    // Compare the two objects and find the changed keys
    const changedKeys = findChangedKeys(props.requirement, formatedFormData);
    if (changedKeys.length === 0) {
      setIsFormChanged(false);
    } else {
      try {
        let reponse: any;
        if (props.requirement?.status === RequirementStatus.Pending) {
          reponse = await api.AddNewRequirement.updateRequirements(
            formatedFormData,
            props.requirement?._id
          );
        } else {
          reponse = await api.AddNewRequirement.createRequirements({
            ...formatedFormData,
            parentId: props.requirement?._id,
          });
        }
        if (reponse) {
          toastr.success("Success", "Requirements Edited Successfully");
          setConfirmResData(reponse);

          props.onClose?.();
          props.onReload?.(reponse._id);
          setApprovalButtonDiabled(false);
        }
      } catch (error) {
        setApprovalButtonDiabled(false);
        toastr.error("Error", "Failed to create requirements");
        // console.log("Failed to create requirements:", error);
      }
      setIsFormChanged(true);
    }
    setSaving(false);
  };

  return (
    <Fragment>
      <EditRequirementForm
        onSubmit={onSubmit}
        onClose={props?.onClose}
        requirement={props.requirement}
        isRequirementEdit={isRequirementEdit}
        cities={cities}
        microMarketsData={microMarketsData}
        preferredLocationsData={preferredLocationsData}
        projectType={projectType}
        propertyType={propertyType}
        preferredDevelopers={preferredDevelopers}
        floorPreferences={floorPreferences}
        preferredProject={preferredProject}
        amenities={amenities}
        facilities={facilities}
        handleCityZones={handleCityZones}
        isFormChanged={isFormChanged}
        onFormChanged={() => setIsFormChanged(true)}
        loadingStates={loadingStates}
        saving={saving}
      />
    </Fragment>
  );
};

export default EditRequirement;
